import React, { useMemo, useState } from 'react';
import WeeklyFlowComboChart from './WeeklyFlowComboChart';
import './WeeklySnapshotPanel.css';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const toTimestamp = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const normalized = String(value).trim().replace(/\./g, '-');
  const dt = new Date(normalized);
  const time = dt.getTime();
  if (!Number.isNaN(time)) {
    return time;
  }
  return Number.NEGATIVE_INFINITY;
};

const formatDateLabel = (value) => {
  if (!value) return '-';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text.slice(2, 4)}.${text.slice(5, 7)}.${text.slice(8, 10)}`;
  }
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(2, 4)}.${text.slice(4, 6)}.${text.slice(6, 8)}`;
  }
  return text;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString();
};

const getNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

function WeeklySnapshotPanel({ rows = [], complexName = '' }) {
  const [periodMode, setPeriodMode] = useState('weekly'); // 'weekly' or 'monthly'

  const normalizedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => row && row.baseDate);
  }, [rows]);

  const dateRows = useMemo(() => {
    const grouped = new Map();

    normalizedRows.forEach((row) => {
      let key = String(row.baseDate || '').trim();
      if (!key) return;

      // 월간 모드: YYYY-MM로 그룹핑
      if (periodMode === 'monthly') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
          key = key.slice(0, 7); // YYYY-MM
        } else if (/^\d{8}$/.test(key)) {
          key = `${key.slice(0, 4)}-${key.slice(4, 6)}`;
        }
      }

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(row);
    });

    const aggregated = Array.from(grouped.entries()).map(([baseDate, sameDateRows]) => {
      const sum = (picker) => sameDateRows.reduce((acc, item) => acc + (getNumber(picker(item)) || 0), 0);
      const avg = (picker) => {
        const values = sameDateRows
          .map((item) => getNumber(picker(item)))
          .filter((v) => v !== null);
        if (values.length === 0) return null;
        return values.reduce((a, b) => a + b, 0) / values.length;
      };

      const avgSalePrice = avg((item) => item.salePrice);
      const avgLeasePrice = avg((item) => item.leasePrice);
      const leaseRatio = avgSalePrice && avgLeasePrice ? (avgLeasePrice / avgSalePrice) * 100 : null;

      return {
        baseDate,
        salePrice: avgSalePrice,
        leasePrice: avgLeasePrice,
        leaseRatio,
        saleCount: sum((item) => item.saleCount),
        leaseCount: sum((item) => item.leaseCount),
        saleRealPrice: avg((item) => item.saleRealPrice),
        leaseRealPrice: avg((item) => item.leaseRealPrice),
        saleRealCount: sum((item) => item.saleRealCount),
        leaseRealCount: sum((item) => item.leaseRealCount),
      };
    });

    return aggregated.sort((a, b) => toTimestamp(b.baseDate) - toTimestamp(a.baseDate));
  }, [normalizedRows, periodMode]);

  const latest = dateRows.length > 0 ? dateRows[0] : null;

  const chartRows = useMemo(() => {
    const withValue = dateRows.filter(
      (row) => row.salePrice !== null || row.leasePrice !== null || row.saleCount !== null || row.leaseCount !== null
    );
    const sorted = [...withValue].sort((a, b) => toTimestamp(a.baseDate) - toTimestamp(b.baseDate));
    if (sorted.length === 0) {
      return [];
    }

    const latestTs = toTimestamp(sorted[sorted.length - 1]?.baseDate);
    if (!Number.isFinite(latestTs)) {
      return sorted.slice(-52);
    }

    const fromTs = latestTs - ONE_YEAR_MS;
    const oneYearRows = sorted.filter((row) => toTimestamp(row.baseDate) >= fromTs);
    return oneYearRows.length > 0 ? oneYearRows : sorted.slice(-52);
  }, [dateRows]);

  const renderSparkline = (data) => {
    if (!data || data.length === 0) return null;
    const values = data.map(d => d.leaseRatio).filter(v => v !== null && v !== undefined);
    if (values.length === 0) return null;

    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    const points = values.map((value, index) => {
      const x = (index / (values.length - 1)) * 100;
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg className="sparkline" viewBox="0 0 100 30" preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  };

  return (
    <section className="weekly-snapshot-section">
      <div className="section-header">
        <h3>
          {complexName && <span className="complex-name-prefix">{complexName}</span>}
          {periodMode === 'weekly' ? '주별' : '월별'} 수급 및 가격 동향
        </h3>
        <div className="section-header-right">
          <div className="period-toggle">
            <button
              type="button"
              className={periodMode === 'weekly' ? 'active' : ''}
              onClick={() => setPeriodMode('weekly')}
            >
              주간
            </button>
            <button
              type="button"
              className={periodMode === 'monthly' ? 'active' : ''}
              onClick={() => setPeriodMode('monthly')}
            >
              월간
            </button>
          </div>
          <span className="section-caption">최근 1년</span>
        </div>
      </div>

      {chartRows.length > 0 ? (
        <div className="weekly-chart-card">
          <div className="weekly-chart-head">
            <div className="weekly-legend">
              <span className="dot sale-line" /> 매매가
              <span className="dot lease-line" /> 전세가
              <span className="dot ratio-line" /> 전세가율
              <span className="dot sale-dot" /> 매물수
              <span className="dot lease-dot" /> 전세수
            </div>
          </div>
          <WeeklyFlowComboChart rows={chartRows} />
        </div>
      ) : null}

      {dateRows.length === 0 ? (
        <p className="status-text">등록된 주간 데이터가 없습니다.</p>
      ) : (
        <div className="weekly-table-wrap">
          <table className="weekly-table">
            <thead>
              <tr>
                <th>기준일</th>
                <th>매매가</th>
                <th>전세가</th>
                <th>매물수</th>
                <th>전세수</th>
                <th>매매실거래가</th>
                <th>전세실거래가</th>
                <th>매매실거래건수</th>
                <th>전세실거래건수</th>
              </tr>
            </thead>
            <tbody>
              {dateRows.map((row, idx) => (
                <tr key={`${row.baseDate}-${idx}`}>
                  <td>{formatDateLabel(row.baseDate)}</td>
                  <td>{formatNumber(row.salePrice)}</td>
                  <td>{formatNumber(row.leasePrice)}</td>
                  <td>{formatNumber(row.saleCount)}</td>
                  <td>{formatNumber(row.leaseCount)}</td>
                  <td>{formatNumber(row.saleRealPrice)}</td>
                  <td>{formatNumber(row.leaseRealPrice)}</td>
                  <td>{formatNumber(row.saleRealCount)}</td>
                  <td>{formatNumber(row.leaseRealCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default WeeklySnapshotPanel;
